import {
  createStoreSlice as createSlice,
  type PayloadAction,
} from '@/store/core';
import { RootState } from '@/store';
import { StoreError } from '@/types/storeError.interface';
import { ISection, IndexedSections } from '@/typings';

interface SectionState {
  /** True while a foreground fetch — i.e. a section switch — is in flight. Drives skeletons. */
  loading: boolean;
  error: StoreError | null;
  sections: IndexedSections;
  currentSectionId: ISection['id'] | null;
}

type SectionSummary = {
  id: ISection['id'];
  requiredFieldsCount: number;
  fieldsCount: number;
  pendingRequiredFieldsCount: number;
  pendingFieldsCount: number;
  restrictionViolationsCount: number;
  completedPercentage: number;
};

const initialState: SectionState = {
  sections: {},
  currentSectionId: null,
  loading: false,
  error: null,
};

export const sectionSlice = createSlice({
  name: 'section',
  initialState,
  reducers: {
    sectionLoading: state => {
      state.loading = true;
    },
    fetchSectionSuccess: (
      state,
      action: PayloadAction<ISection & { background?: boolean }>,
    ) => {
      const { background, ...section } = action.payload;

      state.error = null;

      // A late background refresh of a section the user already navigated away from
      // must not become the "current" one, and must not clear a skeleton the switch it
      // landed during is still waiting on — that would show the new section's stale
      // cached values until its own response arrives.
      if (!background) {
        state.loading = false;
        state.currentSectionId = section.id;
      }

      state.sections[section.id] = {
        ...(state.sections?.[section.id] ?? {}),
        ...section,
      };
    },
    // Deliberately leaves `loading` alone: this carries the sidebar's section list, not
    // the section being opened, and clearing the flag here would drop the skeleton while
    // that section's own request is still in flight.
    fetchApplicationSectionsSuccess: (
      state,
      action: PayloadAction<ISection[]>,
    ) => {
      state.error = null;
      action.payload.forEach(section => {
        state.sections[section.id] = {
          ...(state.sections?.[section.id] ?? {}),
          ...section,
        };
      });
    },
    fetchSectionSummarySuccess: (
      state,
      action: PayloadAction<SectionSummary>,
    ) => {
      state.error = null;
      state.sections[action.payload.id] = {
        ...state.sections?.[action.payload.id],
        ...action.payload,
      };
    },
    fetchSectionFailure: (state, action: PayloadAction<StoreError>) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Deliberately leaves `loading` alone, like a background refresh's success: a save or
    // background refresh that fails mid-switch must not drop the skeleton the switch raised.
    backgroundSectionFailure: (state, action: PayloadAction<StoreError>) => {
      state.error = action.payload;
    },
  },
});

export const {
  sectionLoading,
  fetchSectionSuccess,
  fetchApplicationSectionsSuccess,
  fetchSectionSummarySuccess,
  fetchSectionFailure,
  backgroundSectionFailure,
} = sectionSlice.actions;

export const selectSection = (state: RootState) =>
  state[sectionSlice.name] as typeof initialState;
